/**
 * plaidLinkStorage — Persist Plaid Link handoff state across an OAuth round-trip.
 *
 * Why localStorage (not sessionStorage)?
 *   iOS Safari / installed PWAs frequently drop `sessionStorage` when a
 *   cross-origin OAuth redirect opens a new browser context (Chase, BofA,
 *   Wells Fargo, Revolut US, etc.). Losing the link_token means
 *   `/oauth/plaid` (PlaidOAuthResumePage) can't resume the session and the
 *   user is left with a "session expired" error even though they completed
 *   their bank's login. `localStorage` survives that hop.
 *
 * We add a short TTL (30 min) so a stray token can never be reused by a
 * later, unrelated Link flow, and we always clear it on success/exit.
 *
 * iter1204-plaid-ux: extended with `pending_exchange` marker so /banks can
 * detect an interrupted OAuth round-trip (user closed the tab AFTER bank
 * OAuth but BEFORE /plaid/exchange-token completed) and offer a "Resume
 * Chase connection" banner instead of silently forgetting the attempt.
 */
const KEY_TOKEN = 'plaid_link_token';
const KEY_RETURN = 'plaid_link_return_path';
const KEY_EXPIRES = 'plaid_link_token_expires_at';

// iter1204-plaid-ux: separate marker recording "user started a Plaid
// connect flow that hasn't completed yet". Written when the link_token is
// issued; cleared once token exchange succeeds or the user cancels. If
// still present on the next /banks visit, we show a resume banner.
const KEY_PENDING = 'plaid_pending_connect';

// Plaid link_tokens live 4 hours; we cap our handoff much tighter so a
// forgotten token can't be replayed later.
const TTL_MS = 30 * 60 * 1000;

export function savePlaidLinkToken(token, returnPath, institutionHint) {
  if (!token) return;
  try {
    const expiresAt = Date.now() + TTL_MS;
    localStorage.setItem(KEY_TOKEN, token);
    localStorage.setItem(KEY_RETURN, returnPath || window.location.pathname);
    localStorage.setItem(KEY_EXPIRES, String(expiresAt));
    // iter1204-plaid-ux: also record a pending-connect marker so /banks
    // can surface a "Resume connection" banner if the user closes the
    // browser mid-flow. We do NOT store the token itself in this marker
    // (that stays in KEY_TOKEN); this is metadata only.
    localStorage.setItem(KEY_PENDING, JSON.stringify({
      started_at: new Date().toISOString(),
      expires_at: expiresAt,
      return_path: returnPath || window.location.pathname,
      institution_name: (institutionHint && institutionHint.name) || null,
      phase: 'link_opened',
    }));
  } catch {
    /* localStorage unavailable (private mode); nothing we can do */
  }
}

export function readPlaidLinkToken() {
  try {
    const token = localStorage.getItem(KEY_TOKEN);
    const expiresAt = parseInt(localStorage.getItem(KEY_EXPIRES) || '0', 10);
    if (!token) return { token: null, returnPath: null, expired: false };
    if (expiresAt && Date.now() > expiresAt) {
      // Auto-clear expired
      clearPlaidLinkToken();
      return { token: null, returnPath: null, expired: true };
    }
    const returnPath = localStorage.getItem(KEY_RETURN) || '/banks';
    return { token, returnPath, expired: false };
  } catch {
    return { token: null, returnPath: null, expired: false };
  }
}

export function clearPlaidLinkToken() {
  try {
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_RETURN);
    localStorage.removeItem(KEY_EXPIRES);
    localStorage.removeItem(KEY_PENDING);
  } catch {
    /* no-op */
  }
}

// iter1204-plaid-ux: pending-connect helpers used by MyBanksPage to show
// the "Resume connection" banner and by PlaidOAuthResumePage to advance
// the phase (link_opened → oauth_returned → exchanging → done).
export function readPendingPlaidConnect() {
  try {
    const raw = localStorage.getItem(KEY_PENDING);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires_at) return null;
    if (Date.now() > parsed.expires_at) {
      localStorage.removeItem(KEY_PENDING);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function updatePendingPlaidConnect(patch) {
  try {
    const current = readPendingPlaidConnect() || {};
    const next = { ...current, ...(patch || {}), updated_at: new Date().toISOString() };
    if (!next.expires_at) next.expires_at = Date.now() + TTL_MS;
    localStorage.setItem(KEY_PENDING, JSON.stringify(next));
  } catch {
    /* no-op */
  }
}

export function clearPendingPlaidConnect() {
  try {
    localStorage.removeItem(KEY_PENDING);
  } catch {
    /* no-op */
  }
}
